import {
  Badge,
  Button,
  DatatableCell,
  DatatableRow,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { MoreVerticalIcon, UnplugIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PreResolvedAvatarCell } from '@/shared/ui/data-table';
import { EM_DASH } from '@/shared/utils/metric-format';
import type { ConnectedApp } from '../../model';

const CONNECTED_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

interface ConnectedAppsRowProps {
  app: ConnectedApp;
  onDisconnect: (app: ConnectedApp) => void;
}

export const ConnectedAppsRow = ({ app, onDisconnect }: ConnectedAppsRowProps) => {
  const { t } = useTranslation('admin');
  const actionsLabel = t('connectedApps.connectedAppsTable.actionsFor', {
    clientName: app.clientName,
  });

  return (
    <DatatableRow data-testid="connected-app-row">
      <DatatableCell>
        <PreResolvedAvatarCell
          name={app.clientName}
          avatarUrl={null}
          size="sm"
          className="font-medium"
        />
      </DatatableCell>

      <DatatableCell>
        {app.scopes.length > 0 ? (
          <div className="flex flex-wrap gap-xs">
            {app.scopes.map(({ scope, description }) => (
              <Badge key={scope} variant="outline" roundness="full" size="sm">
                {description}
              </Badge>
            ))}
          </div>
        ) : (
          EM_DASH
        )}
      </DatatableCell>

      <DatatableCell className="text-muted-foreground">
        {new Date(app.connectedAt).toLocaleDateString(undefined, CONNECTED_DATE_FORMAT)}
      </DatatableCell>

      <DatatableCell>
        <DropdownMenu>
          {/* Both triggers hand their props to the same button and both write `data-state`;
              the menu's has to be the outer one, as in `FolderMenu`. */}
          <Tooltip>
            <DropdownMenuTrigger asChild>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="mini" aria-label={actionsLabel}>
                  <MoreVerticalIcon className="size-4" />
                </Button>
              </TooltipTrigger>
            </DropdownMenuTrigger>
            <TooltipContent>{actionsLabel}</TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end">
            <DropdownMenuItem variant="destructive" onSelect={() => onDisconnect(app)}>
              <UnplugIcon />
              {t('connectedApps.connectedAppsTable.disconnect')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </DatatableCell>
    </DatatableRow>
  );
};
